import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { filterJobs } from "../config/Services";
import useKitchenOrderAlert, { KITCHEN_ALERT_SOUND_URL } from "../layouts/ecommerce/KDS/useKitchenOrderAlert";
import { buildKitchenNotifications, loadPendingKitchenNotifications } from "./kitchenNotificationFeed";

const empty = {
  jobs: [], notifications: [], loading: false, error: "", arrivalVersion: 0, incomingJobIds: [],
  needsInteraction: false, enableSound: () => {}, acknowledge: () => {}, refresh: () => {},
  registerWorkload: () => ({ publish: () => {}, fail: () => {}, dispose: () => {} }),
};
// Global (all-screens) polling is off to reduce backend load. Kitchen jobs are only
// fetched by the KDS page, table layout and All KOTs modal; KDS still feeds this
// context through registerWorkload. Set to true to restore app-wide polling.
const GLOBAL_POLLING_ENABLED = false;
const KitchenNotificationsContext = createContext(empty);
export const useKitchenNotifications = () => useContext(KitchenNotificationsContext);
const readContext = () => {
  try {
    const config = JSON.parse(localStorage.getItem("appConfig") || "{}");
    const user = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
    return {
      orgId: localStorage.getItem("orgId"), locationId: localStorage.getItem("locId"), userId: user.uidx,
      enabled: localStorage.getItem("retailType") === "RESTAURANT" &&
        !!config.settings?.DISPLAY_SYSTEM_MODE?.kdsDisplayScreens?.includes("KDS"),
    };
  } catch (_) { return { enabled: false }; }
};

/** Mounted above routes: navigation never creates a second kitchen sound player. */
export function KitchenNotificationsProvider({ children }) {
  const { pathname } = useLocation();
  const [context, setContext] = useState(readContext);
  const [switching, setSwitching] = useState(false);
  const [revision, setRevision] = useState(0);
  const workloadOwner = useRef(null);
  const generation = useRef(0);
  const [snapshot, setSnapshot] = useState({ ...empty, scope: "" });
  const seen = useRef({ scope: "", ids: new Set() });
  const enabled = !switching && context.enabled && context.orgId && context.locationId && context.userId &&
    !/authentication|sign-in|sign-up|login|dual-screen/.test(pathname);
  const scope = JSON.stringify([context.orgId, context.locationId, context.userId, !!enabled]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const currentContext = useRef(null);
  currentContext.current = { context, enabled, scope };
  const ownsPolling = useCallback(() => {
    const owner = workloadOwner.current, current = currentContext.current;
    return !!owner && owner.orgId === current.context.orgId &&
      owner.locationId === current.context.locationId && owner.userId === current.context.userId;
  }, []);
  const refresh = useCallback(() => {
    generation.current += 1;
    setRevision((value) => value + 1);
  }, []);
  const publishJobs = useCallback((jobs, nextScope) => {
    if (seen.current.scope !== nextScope) seen.current = { scope: nextScope, ids: new Set() };
    const incoming = jobs.filter((job) => !seen.current.ids.has(String(job.jobId))).map((job) => job.jobId);
    jobs.forEach((job) => seen.current.ids.add(String(job.jobId)));
    setSnapshot((previous) => ({
      ...previous, scope: nextScope, jobs, loading: false, error: "",
      arrivalVersion: (previous.scope === nextScope ? previous.arrivalVersion : 0) + (incoming.length ? 1 : 0),
      incomingJobIds: incoming.length ? incoming : previous.incomingJobIds.filter((id) => jobs.some((job) => String(job.jobId) === String(id))),
    }));
  }, []);
  const registerWorkload = useCallback(({ orgId, locationId }) => {
    const owner = { orgId, locationId, userId: readContext().userId };
    workloadOwner.current = owner;
    refresh();
    const isCurrent = () => workloadOwner.current === owner && ownsPolling() &&
      currentContext.current.enabled &&
      JSON.stringify(readContext()) === JSON.stringify(currentContext.current.context);
    return {
      publish: (jobs) => {
        if (!isCurrent()) return;
        // Aggregates may contain pending dishes in a partially processing job.
        const pending = jobs.flatMap((job) => {
          if (!job.aggregated) return job.status === "PENDING" ? [job] : [];
          const tasks = (job.tasks || []).filter((task) => (task.taskStatus || task.status) === "PENDING");
          return tasks.length ? [{ ...job, status: "PENDING", tasks }] : [];
        });
        publishJobs(pending, currentContext.current.scope);
      },
      fail: (error) => {
        if (!isCurrent()) return;
        const nextScope = currentContext.current.scope;
        const denied = [401, 403].includes(error?.response?.status);
        setSnapshot((previous) => ({
          ...(denied || previous.scope !== nextScope ? empty : previous), scope: nextScope,
          loading: false, error: error?.message || "Kitchen alerts unavailable.",
        }));
      },
      dispose: () => {
        if (workloadOwner.current !== owner) return;
        workloadOwner.current = null;
        refresh();
      },
    };
  }, [ownsPolling, publishJobs, refresh]);

  const updateContext = useCallback(() => {
    const next = readContext();
    setContext((previous) => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
  }, []);
  useEffect(updateContext, [pathname, updateContext]);
  useEffect(() => {
    const changing = () => { generation.current += 1; setSwitching(true); };
    const loaded = () => { updateContext(); setSwitching(false); };
    window.addEventListener("pos:locationChanging", changing);
    window.addEventListener("pos:rolesLoaded", loaded);
    window.addEventListener("storage", loaded);
    return () => {
      window.removeEventListener("pos:locationChanging", changing);
      window.removeEventListener("pos:rolesLoaded", loaded);
      window.removeEventListener("storage", loaded);
    };
  }, [updateContext]);

  useEffect(() => {
    let cancelled = false, inFlight = false;
    if (seen.current.scope !== scope) seen.current = { scope, ids: new Set() };
    if (!enabled) { setSnapshot({ ...empty, scope }); return; }
    if (ownsPolling()) return;
    if (!GLOBAL_POLLING_ENABLED) { setSnapshot({ ...empty, scope }); return; }
    const read = async () => {
      if (cancelled || inFlight || ownsPolling()) return;
      const latest = readContext();
      if (JSON.stringify(latest) !== JSON.stringify(context)) { setContext(latest); return; }
      inFlight = true;
      const requestGeneration = generation.current;
      const isCurrent = () => !cancelled && currentScope.current === scope && generation.current === requestGeneration &&
        JSON.stringify(readContext()) === JSON.stringify(context);
      setSnapshot((previous) => previous.scope === scope ? { ...previous, loading: true } : { ...empty, scope, loading: true });
      try {
        const jobs = await loadPendingKitchenNotifications(filterJobs, context, isCurrent);
        if (!jobs || !isCurrent()) return;
        publishJobs(jobs, scope);
      } catch (error) {
        if (isCurrent()) {
          const denied = [401, 403].includes(error.response?.status);
          if (denied) alert.acknowledge([]);
          setSnapshot((previous) => ({
            ...(denied ? empty : previous), scope, loading: false,
            error: error.message || "Kitchen alerts unavailable.",
          }));
        }
      } finally { inFlight = false; }
    };
    read();
    const timer = setInterval(read, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [scope, enabled, context, revision, ownsPolling, publishJobs]);

  const active = enabled && snapshot.scope === scope ? snapshot : { ...empty, scope };
  const alert = useKitchenOrderAlert({ jobs: active.jobs, scope, loading: active.loading, error: active.error });
  const acknowledge = useCallback((jobIds = []) => {
    alert.acknowledge(jobIds);
    const ids = new Set(jobIds.map(String));
    jobIds.forEach((id) => seen.current.ids.add(String(id)));
    setSnapshot((previous) => ({
      ...previous,
      jobs: previous.jobs.filter((job) => !ids.has(String(job.jobId))),
      incomingJobIds: previous.incomingJobIds.filter((id) => !ids.has(String(id))),
    }));
    refresh();
  }, [alert.acknowledge, refresh]);
  const notifications = useMemo(() => buildKitchenNotifications(active.jobs, scope), [active.jobs, scope]);
  return <KitchenNotificationsContext.Provider value={{
    ...active, notifications, acknowledge, refresh, registerWorkload,
    needsInteraction: alert.needsInteraction, enableSound: alert.enableSound,
  }}>
    <audio ref={alert.audioRef} src={KITCHEN_ALERT_SOUND_URL} preload="auto" aria-hidden="true" style={{ display: "none" }} />
    {children}
  </KitchenNotificationsContext.Provider>;
}
